import { format } from 'date-fns';

import { formatDateToUTC } from '../../shared/utilities/date';
import { getEnvRequired } from '../../shared/utilities/env';
import { getAuthToken } from '../login/operations';

import {
    AttachmentType,
    DiscussionIsResponseRequiredState,
    DiscussionItem,
    Message,
    MessageAttachment,
    MessagesGroupByDate,
} from './types';

export const generateSendingMessageId = (): string => {
    return String(new Date().getTime());
};

/**
 * Generates a unique identifier for attachment tracking during upload.
 *
 * Combines filename with timestamp and random suffix to ensure uniqueness
 * when multiple files are uploaded simultaneously.
 *
 * @param filename - The name of the file being uploaded
 * @returns A unique identifier string in format: `{filename}-{timestamp}-{randomSuffix}`
 *
 * @example
 * generateAttachmentId('document.pdf')
 * // Returns: 'document.pdf-1706025600000-a3x8k9m2p'
 */
export const generateAttachmentId = (filename: string): string => {
    const timestamp = new Date().getTime();
    // eslint-disable-next-line sonarjs/pseudo-random -- Math.random is intentional, no security concern here
    const randomSuffix = Math.random().toString(36).substring(2, 11);
    return `${filename}-${timestamp}-${randomSuffix}`;
};

export const addAttachmentLinkToMessage = (message: Message): Message => ({
    ...message,
    attachments: message.attachments.map((attachment) => ({
        ...attachment,
        link: `${getEnvRequired('DISCUSSION_API_URL')}/discussions/messages/${
            message.id
        }/attachments/${attachment.id}?jwt=${getAuthToken()}`,
    })),
});

export const groupDiscussionItemByDate = (
    acc: MessagesGroupByDate,
    discussionItem: DiscussionItem,
): MessagesGroupByDate => {
    const date = format(discussionItem.sendDate, 'yyyy-MM-dd');
    acc[date] = [...(acc[date] || []), discussionItem];
    return acc;
};

const IMAGE_TYPES: AttachmentType[] = [AttachmentType.JPG, AttachmentType.PNG];

export const categorizeAttachments = (
    attachments: MessageAttachment[],
): { tipiLinks: MessageAttachment[]; images: MessageAttachment[]; files: MessageAttachment[] } => ({
    tipiLinks: attachments.filter((a) => a.type === AttachmentType.TIPI),
    images: attachments.filter((a) => IMAGE_TYPES.includes(a.type)),
    files: attachments.filter(
        (a) => a.type !== AttachmentType.TIPI && !IMAGE_TYPES.includes(a.type),
    ),
});

const FILE_TYPE_LABELS: Partial<Record<AttachmentType, string>> = {
    [AttachmentType.PDF]: 'PDF',
    [AttachmentType.DOCX]: 'Word',
    [AttachmentType.TXT]: 'TXT',
};

export const getFileTypeLabel = (type: AttachmentType): string =>
    FILE_TYPE_LABELS[type] ?? type.toUpperCase();

// eslint-disable-next-line sonarjs/slow-regex -- date pattern with known bounded input, no ReDoS risk
const TIPI_NAME_PATTERN = /^(\d{4}-\d{2}-\d{2})\s*-\s*(.+)$/;

export const parseTipiAttachmentName = (name: string): { date: Date | null; label: string } => {
    const match = TIPI_NAME_PATTERN.exec(name);
    if (!match) return { date: null, label: name };

    const [, dateStr, label] = match;
    const date = formatDateToUTC(dateStr);
    if (!date || Number.isNaN(date.getTime())) return { date: null, label: name };

    return { date, label: label.trim() };
};

export const getIsResponseRequiredState = (
    isLastMessageFromTraveller = false,
    isResponseRequired: boolean,
): DiscussionIsResponseRequiredState => {
    if (isLastMessageFromTraveller && isResponseRequired) {
        return 'TO_MARK_AS_NO_RESPONSE_REQUIRED';
    }
    if (isLastMessageFromTraveller && !isResponseRequired) {
        return 'MANUALLY_MARKED_AS_NO_RESPONSE_REQUIRED';
    }
    return 'AUTOMATICALLY_MARKED_AS_NO_RESPONSE_REQUIRED';
};
